import type { FileDiffHunk } from "@/lib/chat";

type DiffLineKind = "added" | "removed" | "context";

// 行頭の帯は inset の影で描く。border だとグリッドが 3px ずれて文脈行と桁が揃わなくなる。
const LINE_CLASS: Record<DiffLineKind, string> = {
	added: "bg-diff-added-bg shadow-[inset_3px_0_0_var(--diff-added-marker)]",
	removed: "bg-diff-removed-bg shadow-[inset_3px_0_0_var(--diff-removed-marker)]",
	context: "bg-diff-unchanged-bg",
};

const LINE_NUMBER_CLASS: Record<DiffLineKind, string> = {
	added: "text-diff-added-fg",
	removed: "text-diff-removed-fg",
	context: "text-diff-unchanged-fg",
};

function lineKind(line: string): DiffLineKind {
	if (line.startsWith("+")) return "added";
	if (line.startsWith("-")) return "removed";
	return "context";
}

function lineContent(line: string): string {
	return line.slice(1);
}

function Hunk({
	hunk,
	lineNumbers,
	wrap,
}: {
	hunk: FileDiffHunk;
	lineNumbers: boolean;
	wrap: boolean;
}) {
	let oldLine = hunk.oldStart;
	let newLine = hunk.newStart;
	const whitespace = wrap ? "whitespace-pre-wrap wrap-anywhere" : "whitespace-pre";

	return (
		<div className="overflow-x-auto rounded-2 shadow-[inset_0_0_0_1px_var(--line-1)]">
			<div className={`${wrap ? "" : "min-w-max"} font-mono text-caption leading-normal`}>
				{hunk.lines.map((line, index) => {
					const kind = lineKind(line);
					const currentLine = kind === "removed" ? oldLine : newLine;
					if (kind === "removed") oldLine++;
					else if (kind === "added") newLine++;
					else {
						oldLine++;
						newLine++;
					}
					return (
						<div
							key={index}
							className={`${lineNumbers ? "grid grid-cols-[3rem_minmax(0,1fr)]" : ""} ${LINE_CLASS[kind]}`}
						>
							{lineNumbers && (
								<span className={`select-none px-2 text-right ${LINE_NUMBER_CLASS[kind]}`}>
									{currentLine}
								</span>
							)}
							{/* 空行も 1 行分の高さを取り、消えて見えないようにする。 */}
							<code
								className={`block min-h-lh ${whitespace} ${lineNumbers ? "pr-2" : "px-3"} text-fg-2`}
							>
								{lineContent(line)}
							</code>
						</div>
					);
				})}
			</div>
		</div>
	);
}

/** ファイル編集ツールが返した hunk を、行番号と追加・削除の色分け付きで表示する。 */
export function DiffHunkList({
	hunks,
	truncated = false,
	lineNumbers = true,
	wrap = false,
}: {
	hunks: FileDiffHunk[];
	truncated?: boolean;
	/** 行の位置が分からない差分 (実行前の置換など) では false にして行番号を出さない。 */
	lineNumbers?: boolean;
	/** 長い行を横スクロールにせず折り返す。 */
	wrap?: boolean;
}) {
	return (
		<div data-testid="diff-hunk-list" className="grid gap-2">
			{hunks.map((hunk, index) => (
				<div key={index} className="grid gap-2">
					{index > 0 && <div className="text-center font-mono text-caption text-fg-3">⋯</div>}
					<Hunk hunk={hunk} lineNumbers={lineNumbers} wrap={wrap} />
				</div>
			))}
			{hunks.length === 0 && !truncated && (
				<p className="m-0 text-caption leading-normal text-fg-3">変更はありません</p>
			)}
			{truncated && (
				<p className="m-0 text-caption leading-normal text-fg-3">
					差分が大きいため、一部のみ表示しています。
				</p>
			)}
		</div>
	);
}
